<script setup lang="ts">
import type { SearchBrandResultProps } from './SearchBrandResult.props.ts'

const props = withDefaults(defineProps<SearchBrandResultProps>(), {
  total: 0,
  isVisible: true,
  page: 0,
  xseQuery: false,
})

const emit = defineEmits<{
  (e: 'load-more'): void
  (e: 'set-pagination', brand: string, page: number): void
}>()

const { ts } = useI18n()
const { dispatchInternalSearchExpansion } = useGAInternalSearchEvents()

const { breakpoint } = useProjectBreakpoint()
const isMobile = breakpoint.smaller('lg')
const isLargeScreen = breakpoint.greaterOrEqual('lg-custom')

const PREVIEW_ITEMS = isMobile.value ? 4 : isLargeScreen.value ? 5 : 4

const items = computed(() => {
  if (!props.showMore) return props.products.slice(0, PREVIEW_ITEMS)
  else return props.products
})
const handlePreview = () => {
  emit('load-more')
  dispatchInternalSearchExpansion(props?.searchResult ?? '', props?.id)
}
</script>

<template>
  <div class="flex flex-col">
    <div
      class="text-book-6 flex pb-4"
      :class="
        searchResult
          ? `bg-neutral-white shadow-neutral-white sticky z-40 shadow-[0_0_0_1px]`
          : ''
      "
      :style="{ top: searchResult ? `${searchHeight}px` : '' }"
    >
      <span class="flex grow items-center">
        <!-- @vue-skip megaLogoMap is not auto importing-->
        <component
          :is="menuLogoMap[id as MenuLogoType]"
          class="mr-2 inline-block h-5 w-5"
        />
        <h5 class="inline uppercase">
          <span>{{ name }}</span> <span v-if="total">({{ total }})</span>
        </h5>
      </span>
    </div>
    <div>
      <ul
        :class="[
          'search-grid lg-custom:grid-cols-5 grid grid-cols-2 gap-[1px] md:grid-cols-3 lg:grid-cols-4',
          { 'search-grid--preview': !showMore },
        ]"
      >
        <li v-for="(item, index) in items" :key="item?.objectID ?? index">
          <WrapperProductTile :product="item">
            <template #default="{ product }">
              <UIProductTile
                xse-type="rec"
                :product="{
                  ...product,
                  linkToProduct: `${product.linkToProduct}?xse_prod_code=${product.sku}&xse=${predictionId}`,
                }"
                :xse-query="xseQuery ? searchResult : null"
                :xse-prod-code="product.sku"
                :data-xse-prod-code="product.sku"
                :show-cta-actions="false"
                class="search-grid__item col-span-1 h-full"
                @click="() => resultClickHandler?.(product.sku)"
              />
            </template>
          </WrapperProductTile>
        </li>
      </ul>
      <div
        v-if="showMore && items.length < total"
        class="my-lg flex justify-center"
      >
        <AtomsCta
          :loading="isLoading"
          @click-handler="emit('set-pagination', id, page + 1)"
        >
          <template #label>
            {{
              ts('search.results.showMore', {
                brand: name,
                current: items.length,
                total,
              })
            }}
          </template>
        </AtomsCta>
      </div>
      <div
        v-if="products.length > PREVIEW_ITEMS"
        class="my-lg flex justify-center"
      >
        <AtomsCta
          :aria-label="
            showMore
              ? $ts('aria-labels.search.showLess')
              : $ts('aria-labels.search.showAll')
          "
          :anatomy="showMore ? 'link' : 'primary'"
          @click="handlePreview()"
        >
          <template #label>
            {{
              !searchResult
                ? showMore
                  ? ts('search.results.showLess')
                  : ts('search.results.showAll')
                : showMore
                  ? ts('search.results.showLess')
                  : ts('search.results.showMore', {
                      brand: name,
                      current: items.length,
                      total,
                    })
            }}
          </template>
        </AtomsCta>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.search-grid {
  &__item {
    box-shadow: 0 0 0 1px var(--colorStrokeCardDefault);

    .outline-stroke-cards-hover {
      position: relative;

      &:hover {
        outline: none !important;

        &::after {
          content: '';
          position: absolute;
          inset: 0;
          border: 1px solid var(--colorStrokeCardHover);
          z-index: 10;
          pointer-events: none;
        }
      }
    }
  }

  &--preview {
    .search-grid__item {
      display: none;

      &:nth-child(-n + 2) {
        display: block;
      }

      @screen md {
        &:nth-child(-n + 3) {
          display: block;
        }
      }

      @screen lg {
        &:nth-child(-n + 4) {
          display: block;
        }
      }
    }
  }
}
</style>
